import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Trash2,
  Edit,
  ChevronLeft,
  Package,
  Truck,
  Info,
  Scale,
  DollarSign,
  Box,
  MapPin,
  Star,
  Store,
  Phone,
  Mail,
  Heart,
  Share2,
  ShoppingCart,
  CheckCircle,
  XCircle,
  Camera,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import ToggleAvailabilityButton from "../AProductUpdat/ToggleAvailabilityButton";

// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const BackendUrl = process.env.REACT_APP_Backend_Url;

const ProductDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeImage, setActiveImage] = useState("");
  const [product, setProduct] = useState(null);
  const [categorie, setCategorie] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isAvailable, setIsAvailable] = useState(product?.isdisponible);

  useEffect(() => {
    fetchProductData();
  }, [id]);

  const fetchProductData = async () => {
    try {
      const [productRes, categoriesRes] = await Promise.all([
        axios.get(`${BackendUrl}/api/productsRoutes/${id}`),
        axios.get(`${BackendUrl}/api/categoriesRoutes`),
      ]);

      const productData = productRes.data.data;
      setProduct(productData);
      setActiveImage(productData.image1);

      // Find the matching category
      const matchingCategory = categoriesRes?.data?.data?.find(
        (item) => item?.ClefCategorie === productData?.ClefCategorie
      );
      setCategorie(matchingCategory);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) {
      setIsLoading(true);
      try {
        await axios.delete(`${BackendUrl}/api/productsRoutes/delete/${id}`);
        toast.success("Produit supprimé avec succès");
        navigate("/Admin/Products");
      } catch (error) {
        toast.error("Erreur lors de la suppression");
      }
      setIsLoading(false);
    }
  };

  const renderImageGallery = () => {
    const images = [product.image1, product.image2, product.image3].filter(
      Boolean
    );

    return (
      <Card
        className="overflow-hidden border-0 shadow-xl rounded-xl"
        style={{ borderLeft: `4px solid ${COLORS.orange}` }}
      >
        <CardContent className="p-0">
          <div className="relative group">
            <div className="relative h-[450px] overflow-hidden">
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <Camera className="h-12 w-12 text-gray-300 animate-pulse" />
                </div>
              )}
              <img
                src={activeImage}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                style={{ display: imageLoaded ? "block" : "none" }}
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  setImageLoaded(true);
                  e.target.src = "/api/placeholder/600/450";
                }}
              />
              {product.prixPromo > 0 && (
                <div className="absolute top-4 right-4">
                  <Badge
                    className="text-white font-bold px-3 py-2"
                    style={{ backgroundColor: COLORS.orange }}
                  >
                    PROMO!
                  </Badge>
                </div>
              )}
            </div>

            <div className="absolute inset-x-0 top-0 h-full bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="absolute inset-x-0 bottom-0 flex justify-center p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full flex space-x-6">
                <button className="flex items-center space-x-1 text-gray-700 hover:text-red-500 transition-colors">
                  <Heart className="h-5 w-5" />
                  <span className="text-sm font-medium">Favoris</span>
                </button>
                <button className="flex items-center space-x-1 text-gray-700 hover:text-blue-500 transition-colors">
                  <Share2 className="h-5 w-5" />
                  <span className="text-sm font-medium">Partager</span>
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 bg-white">
            <div className="grid grid-cols-5 gap-2">
              {images?.map((img, idx) => (
                <button
                  key={idx}
                  className={`relative overflow-hidden rounded-lg transition-all ${
                    activeImage === img
                      ? "ring-2 ring-opacity-100"
                      : "opacity-70 hover:opacity-100"
                  }`}
                  style={{
                    ring:
                      activeImage === img
                        ? `2px solid ${COLORS.orange}`
                        : "none",
                    transform: activeImage === img ? "scale(1.05)" : "scale(1)",
                  }}
                  onClick={() => setActiveImage(img)}
                >
                  <img
                    src={img}
                    alt={`Vue ${idx + 1}`}
                    className="h-16 w-full object-cover"
                    onError={(e) => {
                      e.target.src = "/api/placeholder/100/100";
                    }}
                  />
                </button>
              ))}

              {/* Placeholder thumbnails for empty slots */}
              {Array.from({ length: Math.max(0, 5 - images.length) })?.map(
                (_, idx) => (
                  <div
                    key={`empty-${idx}`}
                    className="h-16 w-full rounded-lg bg-gray-100 flex items-center justify-center"
                  >
                    <Camera className="h-4 w-4 text-gray-300" />
                  </div>
                )
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderVariants = () => {
    if (!product.variants || product.variants.length === 0) return null;

    return (
      <Card
        className="border-0 shadow-lg rounded-xl"
        style={{ borderTop: `3px solid ${COLORS.green}` }}
      >
        <CardHeader className="pb-2">
          <CardTitle
            className="flex items-center text-xl"
            style={{ color: COLORS.darkGreen }}
          >
            <Package className="mr-2 h-5 w-5" />
            Variantes disponibles
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {product.variants?.map((variant, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-lg transition-all hover:shadow-md cursor-pointer"
              >
                <img
                  src={variant.imageUrl || "/api/placeholder/200/150"}
                  alt={variant.color}
                  className="w-full h-28 object-cover transition-transform group-hover:scale-105"
                  onError={(e) => {
                    e.target.src = "/api/placeholder/200/150";
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 backdrop-blur-sm">
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-3 h-3 rounded-full border border-white"
                        style={{ backgroundColor: variant.colorCode || "#ccc" }}
                      />
                      <span className="capitalize text-sm">
                        {variant.color}
                      </span>
                    </div>
                    <Badge
                      variant="outline"
                      className="text-xs border-0"
                      style={{
                        backgroundColor:
                          variant.stock > 0
                            ? COLORS.lightGreen
                            : COLORS.lightOrange,
                        color: variant.stock > 0 ? COLORS.green : COLORS.orange,
                      }}
                    >
                      {variant.stock > 0 ? `Stock: ${variant.stock}` : "Épuisé"}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen p-6"
        style={{ backgroundColor: COLORS.paleGreen }}
      >
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-10 w-24 mb-6" />
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Skeleton className="h-[450px] w-full rounded-xl" />
              <div className="grid grid-cols-5 gap-2">
                {[...Array(5)]?.map((_, idx) => (
                  <Skeleton key={idx} className="h-16 w-full rounded-lg" />
                ))}
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <Skeleton className="h-10 w-3/4 mb-2" />
                <Skeleton className="h-6 w-1/4 mb-4" />
                <Skeleton className="h-8 w-1/3 mb-6" />
              </div>
              <Skeleton className="h-12 w-full mb-4" />
              <Skeleton className="h-64 w-full rounded-xl" />
              <div className="grid grid-cols-2 gap-4">
                <Skeleton className="h-12 w-full rounded-full" />
                <Skeleton className="h-12 w-full rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <ScrollArea
      className="h-screen"
      style={{ backgroundColor: COLORS.paleGreen }}
    >
      <div className="min-h-screen p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          {/* ///////////////////////////////////////////////////////// */}
          <div className="border rounded-xl p-4 shadow flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500">
                Statut : {isAvailable ? "Disponible" : "Indisponible"}
              </p>
            </div>
            <ToggleAvailabilityButton
              productId={product?._id}
              // currentStatus={product?.isdisponible}
              onSuccess={(newStatus) =>
                console.log("Nouveau statut:", newStatus)
              }
              isAvailable={isAvailable}
              setIsAvailable={setIsAvailable}
            />
          </div>
          {/* ///////////////////////////////////////////////////////// */}
          <Button
            variant="ghost"
            className="mb-6 hover:bg-white/60 transition-colors backdrop-blur-sm rounded-full"
            onClick={() => navigate(-1)}
            style={{ color: COLORS.darkGreen }}
          >
            <ChevronLeft className="mr-2" /> Retour aux produits
          </Button>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
            {/* Left Column - Image Gallery Section */}
            <div className="space-y-6">
              {renderImageGallery()}
              {renderVariants()}
            </div>

            {/* Right Column - Product Information Section */}
            <div className="space-y-6">
              <Card
                className="border-0 shadow-xl rounded-xl overflow-hidden"
                style={{ borderRight: `4px solid ${COLORS.green}` }}
              >
                <CardHeader
                  className="pb-3"
                  style={{ borderBottom: `1px solid ${COLORS.lightGray}` }}
                >
                  <div className="space-y-4">
                    <div className="flex flex-wrap justify-between items-start gap-2">
                      <CardTitle
                        className="text-2xl sm:text-3xl font-bold"
                        style={{ color: COLORS.gray }}
                      >
                        {product.name}
                      </CardTitle>
                      <div className="flex flex-wrap gap-2">
                        {categorie && (
                          <Badge
                            className="text-sm font-medium"
                            style={{
                              backgroundColor: COLORS.green,
                              color: COLORS.white,
                            }}
                          >
                            {categorie.name}
                          </Badge>
                        )}
                        <Badge
                          className="text-sm font-medium"
                          style={{
                            backgroundColor: COLORS.lightGray,
                            color: COLORS.gray,
                          }}
                        >
                          {product.marque || "Non spécifié"}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="flex text-yellow-400 mr-2">
                        {[...Array(5)]?.map((_, i) => (
                          <Star key={i} size={18} fill="currentColor" />
                        ))}
                      </div>
                      <span className="text-sm text-gray-500">
                        5.0 (12 avis)
                      </span>
                    </div>

                    <div className="flex items-baseline space-x-4">
                      <span
                        className="text-3xl font-bold"
                        style={{ color: COLORS.orange }}
                      >
                        {product.prixPromo > 0
                          ? product.prixPromo
                          : product.prix}{" "}
                        €
                      </span>
                      {product.prixPromo > 0 && (
                        <span className="text-xl line-through text-gray-400">
                          {product.prix} €
                        </span>
                      )}
                      {product.prixPromo > 0 && (
                        <Badge
                          className="text-sm"
                          style={{
                            backgroundColor: COLORS.lightOrange,
                            color: COLORS.orange,
                          }}
                        >
                          -
                          {Math.round(
                            ((product.prix - product.prixPromo) /
                              product.prix) *
                              100
                          )}
                          %
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center space-x-3">
                      <Badge
                        className="text-sm px-3 py-1"
                        style={{
                          backgroundColor:
                            product.quantite > 0
                              ? COLORS.lightGreen
                              : COLORS.lightOrange,
                          color:
                            product.quantite > 0 ? COLORS.green : COLORS.orange,
                        }}
                      >
                        {product.quantite > 0 ? (
                          <CheckCircle className="h-4 w-4 mr-1" />
                        ) : (
                          <XCircle className="h-4 w-4 mr-1" />
                        )}
                        {product.quantite > 0 ? "En stock" : "Épuisé"}
                      </Badge>

                      {product.quantite > 0 && (
                        <span className="text-sm text-gray-500">
                          {product.quantite} unités disponibles
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-4">
                  <Tabs
                    defaultValue="details"
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="w-full"
                  >
                    <TabsList className="w-full grid grid-cols-2 gap-4 mb-4">
                      <TabsTrigger
                        value="details"
                        className="font-medium text-sm rounded-full"
                        style={{
                          backgroundColor:
                            activeTab === "details"
                              ? COLORS.green
                              : COLORS.white,
                          color:
                            activeTab === "details"
                              ? COLORS.white
                              : COLORS.gray,
                          border: `1px solid ${COLORS.green}`,
                        }}
                      >
                        <Info className="mr-2 h-4 w-4" />
                        Détails du produit
                      </TabsTrigger>
                      <TabsTrigger
                        value="shipping"
                        className="font-medium text-sm rounded-full"
                        style={{
                          backgroundColor:
                            activeTab === "shipping"
                              ? COLORS.orange
                              : COLORS.white,
                          color:
                            activeTab === "shipping"
                              ? COLORS.white
                              : COLORS.gray,
                          border: `1px solid ${COLORS.orange}`,
                        }}
                      >
                        <Truck className="mr-2 h-4 w-4" />
                        Livraison
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="details" className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div
                          className="p-4 rounded-lg"
                          style={{ backgroundColor: COLORS.paleGreen }}
                        >
                          <Store
                            className="h-5 w-5 mb-2"
                            style={{ color: COLORS.green }}
                          />
                          <p className="text-sm text-gray-500">Marque</p>
                          <p className="font-medium">
                            {product.marque || "Non spécifié"}
                          </p>
                        </div>
                        <div
                          className="p-4 rounded-lg"
                          style={{ backgroundColor: COLORS.lightOrange }}
                        >
                          <Box
                            className="h-5 w-5 mb-2"
                            style={{ color: COLORS.orange }}
                          />
                          <p className="text-sm text-gray-500">Stock</p>
                          <p className="font-medium">
                            {product.quantite} unités
                          </p>
                        </div>
                      </div>

                      <div className="mt-6">
                        <h3
                          className="text-lg font-medium mb-3"
                          style={{ color: COLORS.darkGreen }}
                        >
                          Description
                        </h3>
                        <div
                          className="prose max-w-none bg-white p-4 rounded-lg shadow-sm"
                          style={{
                            border: `1px solid ${COLORS.lightGray}`,
                            color: COLORS.gray,
                          }}
                          dangerouslySetInnerHTML={{
                            __html:
                              product.description ||
                              "<p>Aucune description disponible</p>",
                          }}
                        />
                      </div>
                    </TabsContent>

                    <TabsContent value="shipping" className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div
                          className="p-4 rounded-lg text-center"
                          style={{ backgroundColor: COLORS.paleGreen }}
                        >
                          <Scale
                            className="h-6 w-6 mx-auto mb-2"
                            style={{ color: COLORS.green }}
                          />
                          <p className="text-sm text-gray-500">Poids</p>
                          <p className="font-medium">
                            {product.shipping?.weight || "N/A"} kg
                          </p>
                        </div>
                        <div
                          className="p-4 rounded-lg text-center"
                          style={{ backgroundColor: COLORS.paleGreen }}
                        >
                          <Box
                            className="h-6 w-6 mx-auto mb-2"
                            style={{ color: COLORS.green }}
                          />
                          <p className="text-sm text-gray-500">Dimensions</p>
                          <p className="font-medium">
                            {product.shipping?.dimensions
                              ? `${product.shipping.dimensions.length} x ${product.shipping.dimensions.width} x ${product.shipping.dimensions.height} cm`
                              : "N/A"}
                          </p>
                        </div>
                        <div
                          className="p-4 rounded-lg text-center"
                          style={{ backgroundColor: COLORS.paleGreen }}
                        >
                          <MapPin
                            className="h-6 w-6 mx-auto mb-2"
                            style={{ color: COLORS.green }}
                          />
                          <p className="text-sm text-gray-500">Origine</p>
                          <p className="font-medium">
                            {product.shipping?.origine || "Non spécifié"}
                          </p>
                        </div>
                      </div>

                      {product.shipping?.zones &&
                      product.shipping.zones.length > 0 ? (
                        <div className="mt-6">
                          <h3
                            className="text-lg font-medium mb-4"
                            style={{ color: COLORS.orange }}
                          >
                            Zones de livraison
                          </h3>
                          <Accordion
                            type="single"
                            collapsible
                            className="w-full"
                          >
                            {product.shipping.zones?.map((zone, idx) => (
                              <AccordionItem
                                key={idx}
                                value={`zone-${idx}`}
                                className="mb-3 border-0"
                              >
                                <AccordionTrigger
                                  className="hover:bg-orange-50 p-3 rounded-lg flex"
                                  style={{
                                    backgroundColor: COLORS.lightOrange,
                                    color: COLORS.orange,
                                    borderLeft: `3px solid ${COLORS.orange}`,
                                  }}
                                >
                                  <div className="flex items-center space-x-2">
                                    <MapPin className="h-4 w-4" />
                                    <span className="font-medium">
                                      {zone.name}
                                    </span>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent
                                  className="p-4 rounded-lg mt-2 border-l-2"
                                  style={{
                                    backgroundColor: COLORS.white,
                                    borderLeftColor: COLORS.orange,
                                  }}
                                >
                                  <div className="space-y-3">
                                    <div className="flex items-center space-x-2">
                                      <Truck className="h-4 w-4 text-gray-500" />
                                      <span>
                                        {zone.transporteurName ||
                                          "Transporteur non spécifié"}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <Phone className="h-4 w-4 text-gray-500" />
                                      <span>
                                        {zone.transporteurContact ||
                                          "Contact non spécifié"}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <DollarSign className="h-4 w-4 text-gray-500" />
                                      <span>
                                        Frais de base: {zone.baseFee || 0} €
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <Scale className="h-4 w-4 text-gray-500" />
                                      <span>
                                        Frais par kg: {zone.weightFee || 0} €
                                      </span>
                                    </div>
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </div>
                      ) : (
                        <div className="bg-white p-4 rounded-lg text-center">
                          <p className="text-gray-500">
                            Aucune information de livraison disponible
                          </p>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant="destructive"
                  className="font-medium rounded-full transition-colors"
                  onClick={handleDelete}
                  disabled={isLoading}
                  style={{
                    backgroundColor: COLORS.orange,
                    color: COLORS.white,
                    boxShadow: "0 4px 6px -1px rgba(232, 126, 4, 0.2)",
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </Button>
                <Button
                  variant="default"
                  className="font-medium rounded-full transition-colors"
                  onClick={() => navigate(`/Admin/ProductUpdat/${id}`)}
                  style={{
                    backgroundColor: COLORS.green,
                    color: COLORS.white,
                    boxShadow: "0 4px 6px -1px rgba(0, 135, 81, 0.2)",
                  }}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Modifier
                </Button>
              </div>

              <Button
                variant="outline"
                className="w-full font-medium rounded-full mt-2 border-dashed"
                onClick={() => navigate("/Admin/Products")}
                style={{
                  borderColor: COLORS.gray,
                  color: COLORS.gray,
                }}
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                Voir tous les produits
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

export default ProductDetail;
